import hash from "object-hash";
import { GeoPoint } from "@rebasepro/types";
//#region src/strings.ts
var tokenizeRegex = /[A-Z]{2,}(?=[A-Z][a-z]|\b)|[A-Z]?[a-z]+|[0-9]+(?:[a-z](?![a-z]))?|[A-Z]/g;
var toKebabCase = (str) => {
	if (!str || typeof str !== "string") return "";
	const regExpMatchArray = str.match(tokenizeRegex);
	if (!regExpMatchArray) return "";
	return regExpMatchArray.map((x) => x.toLowerCase()).join("-");
};
var snakeCaseRegex = tokenizeRegex;
var toSnakeCase = (str) => {
	if (!str || typeof str !== "string") return "";
	const regExpMatchArray = str.match(snakeCaseRegex);
	if (!regExpMatchArray) return "";
	return regExpMatchArray.map((x) => x.toLowerCase()).join("_");
};
function camelCase(str) {
	if (!str) return "";
	if (str.length === 1) return str.toLowerCase();
	const parts = str.split(/[-_ ]+/).filter(Boolean);
	if (parts.length === 0) return "";
	return parts[0].toLowerCase() + parts.slice(1).map((part) => part.charAt(0).toUpperCase() + part.substring(1).toLowerCase()).join("");
}
/**
* A random base-36 string of exactly `strLength` characters.
*
* Not `Math.random().toString(36).slice(2, 2 + strLength)`: that has no
* guaranteed length. Base-36 of a double drops trailing zeros, so the source
* string is short about once in 36 calls and the slice quietly returns fewer
* characters than asked for — `randomString(10)` returning 9. These values
* prefix uploaded filenames to keep them apart, so a short one is a likelier
* collision, and it fails at the rate that makes a test look flaky.
*/
function randomString(strLength = 5) {
	const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";
	let result = "";
	for (let i = 0; i < strLength; i++) result += alphabet.charAt(Math.floor(Math.random() * 36));
	return result;
}
function randomColor() {
	return Math.floor(Math.random() * 16777215).toString(16);
}
function slugify(text, separator = "_", lowercase = true) {
	if (!text) return "";
	const from = "ãàáäâẽèéëêìíïîõòóöôùúüûñç·/_,:;-";
	const to = `aaaaaeeeeeiiiiooooouuuunc${separator}${separator}${separator}${separator}${separator}${separator}${separator}`;
	for (let i = 0, l = 32; i < l; i++) text = text.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
	text = text.toString().trim().replace(/^\s+|\s+$/g, "").replace(/\s+/g, separator).replace(/&/g, separator).replace(/[^\w\\-]+/g, "").replace(new RegExp("\\" + separator + "\\" + separator + "+", "g"), separator);
	return lowercase ? text.toLowerCase() : text;
}
function unslugify(slug) {
	if (!slug) return "";
	if (slug.includes("-") || slug.includes("_") || !slug.includes(" ")) return slug.replace(/[-_]/g, " ").replace(/\w\S*/g, function(txt) {
		return txt.charAt(0).toUpperCase() + txt.substring(1);
	}).trim();
	else return slug.trim();
}
/**
* Are these two identifiers one edit apart, ignoring case?
*
* Tight on purpose. A suggester that fires on two edits offers `columnWidth`
* for `colWith` and `readOnly` for `required`, and a "did you mean" that is
* usually wrong teaches people to stop reading them. One edit covers what
* actually happens — a dropped letter (`multilne`), a doubled one, a transposed
* pair (`validaton`), a wrong case — and nothing else.
*
* Lives here rather than beside either caller because both the CLI's manifest
* reader and the server's collection validator answer the same question about
* an unknown key, and two copies of a similarity rule is two rules the moment
* one of them is tuned.
*/
function isNearMiss(a, b) {
	const x = a.toLowerCase();
	const y = b.toLowerCase();
	if (x === y) return true;
	if (Math.abs(x.length - y.length) > 1) return false;
	const [shorter, longer] = x.length <= y.length ? [x, y] : [y, x];
	let i = 0;
	let j = 0;
	let edits = 0;
	while (i < shorter.length && j < longer.length) {
		if (shorter[i] === longer[j]) {
			i++;
			j++;
			continue;
		}
		if (++edits > 1) return false;
		if (shorter.length === longer.length) i++;
		j++;
	}
	return edits + (longer.length - j) + (shorter.length - i) <= 1;
}
/**
* The key in `known` that `key` was probably meant to be, if any.
*
* `undefined` when nothing is close — which is the common case for a key that
* is simply from a newer version, and the reason the caller must be able to say
* "unknown" without saying "wrong".
*/
function suggestNearMiss(known, key) {
	return known.find((candidate) => isNearMiss(candidate, key));
}
function prettifyIdentifier(input) {
	if (!input) return "";
	let text = input;
	text = text.replace(/([a-z])([A-Z])|([A-Z])([A-Z][a-z])/g, "$1$3 $2$4");
	text = text.replace(/[_-]+/g, " ");
	return text.trim().replace(/\b\w/g, (char) => char.toUpperCase());
}
/**
* How many terms one search string is allowed to contribute.
*
* Every term multiplies the work: the substring fallback compiles to one
* `ILIKE '%term%'` per term *per searchable column*, evaluated per row on a
* sequential scan. Eight is far past what a person types into a search box and
* still bounds a pasted paragraph to a fixed number of predicates. Terms past
* the cap are dropped rather than rejected — dropping only widens the result
* set, and a search box that errors on a long paste is worse than one that
* matches on its first eight words.
*/
var MAX_SEARCH_TERMS = 8;
/**
* The terms a user meant when they typed into a search box.
*
* A search box is not a substring box. Typing `sebastian melendez` on a
* collection whose name is split across `first_name` and `last_name` has to
* find the row, and matching the whole string against each field separately
* cannot: no single column holds both words. Splitting on whitespace and
* requiring *every* term to match *some* field is what does, and it is what
* `websearch_to_tsquery` already does for collections that declared a `search`
* block — so this is also what keeps the two search paths answering the same
* question.
*
* It fixes the trailing space too: `sebastian ` matched nothing at all, because
* the space was part of the pattern.
*
* A double-quoted run is one term, again matching the full-text path, so a
* caller who really does want the phrase can ask for it. An unbalanced quote is
* stripped rather than honoured — the alternative is that everything after a
* stray `"` silently becomes one long term that matches nothing.
*
* Returns an empty array for a string with no terms in it at all (whitespace,
* or nothing but quotes); the caller decides what an empty search means, since
* only it knows whether "no constraint" or "no rows" is the safe reading.
*/
function splitSearchTerms(searchString, maxTerms = 8) {
	const terms = [];
	const pattern = /"([^"]*)"|(\S+)/g;
	let match;
	while ((match = pattern.exec(searchString)) !== null) {
		const term = (match[1] ?? match[2].replace(/"/g, "")).trim();
		if (!term) continue;
		terms.push(term);
		if (terms.length >= maxTerms) break;
	}
	return terms;
}
//#endregion
//#region src/objects.ts
/** @private is the value an empty array? */
var isEmptyArray = (value) => Array.isArray(value) && value.length === 0;
/** @private is the given object a Function? */
var isFunction = (obj) => typeof obj === "function";
/** @private is the given object an integer? */
var isInteger = (obj) => String(Math.floor(Number(obj))) === String(obj);
/** @private is the given object a NaN? */
var isNaN = (obj) => obj !== obj;
/**
* Segments that reach the prototype chain rather than a property of the object.
*
* The twin of this function in `@rebasepro/forms` could be made to write onto
* `Object.prototype` through a path of `__proto__.x`. This copy survives the
* write by accident — its `clone` always spreads into a fresh object, while the
* form engine's has a "preserve class instances" branch that hands back
* `Object.prototype` itself — but `getIn` still *reads* through the chain, and
* handing back `Object.prototype` is how a polluted value is read out again.
*
* Closed on both sides here, so the two implementations agree.
*/
var UNSAFE_PATH_SEGMENTS = /* @__PURE__ */ new Set([
	"__proto__",
	"constructor",
	"prototype"
]);
/** Whether any segment of this path would traverse the prototype chain. */
function pathTraversesPrototype(path) {
	return toPath(path).some((segment) => UNSAFE_PATH_SEGMENTS.has(segment));
}
/**
* Whether writing this single key with `obj[key] = …` would reach the prototype
* chain instead of creating a property.
*
* The single-key counterpart of {@link pathTraversesPrototype}, for the many
* places that copy an object one key at a time. `JSON.parse` creates
* `__proto__` as an *own* property, so it survives `hasOwnProperty` — and then
* `target[key] = value` invokes the setter and replaces the target's prototype.
*/
function isPrototypePollutingKey(key) {
	return UNSAFE_PATH_SEGMENTS.has(key);
}
/**
* Deeply get a value from an object via its path.
*/
function getIn(obj, key, def, p = 0) {
	if (pathTraversesPrototype(key)) return def;
	const path = toPath(key);
	while (obj && p < path.length) obj = obj[path[p++]];
	if (p !== path.length && !obj) return def;
	return obj === void 0 ? def : obj;
}
function setIn(obj, path, value) {
	if (pathTraversesPrototype(path)) return obj;
	const res = clone(obj);
	let resVal = res;
	let i = 0;
	const pathArray = toPath(path);
	for (; i < pathArray.length - 1; i++) {
		const currentPath = pathArray[i];
		const currentObj = getIn(obj, pathArray.slice(0, i + 1));
		if (currentObj && (isObject(currentObj) || Array.isArray(currentObj))) resVal = resVal[currentPath] = clone(currentObj);
		else {
			const nextPath = pathArray[i + 1];
			resVal = resVal[currentPath] = isInteger(nextPath) && Number(nextPath) >= 0 ? [] : {};
		}
	}
	if ((i === 0 ? obj : resVal)[pathArray[i]] === value) return obj;
	if (value === void 0) delete resVal[pathArray[i]];
	else resVal[pathArray[i]] = value;
	if (i === 0 && value === void 0) delete res[pathArray[i]];
	return res;
}
function clone(value) {
	if (Array.isArray(value)) return [...value];
	else if (typeof value === "object" && value !== null) return { ...value };
	else return value;
}
/**
* Deep clone a value, preserving function references and class instances.
* Unlike structuredClone, this handles objects that contain functions
* (e.g. CollectionConfig with target(), childCollections(), callbacks).
*/
function deepClone(value) {
	if (value === null || value === void 0) return value;
	if (typeof value === "function") return value;
	if (typeof value !== "object") return value;
	if (Array.isArray(value)) return value.map((item) => deepClone(item));
	if (Object.getPrototypeOf(value) !== Object.prototype) return value;
	const result = {};
	for (const key of Object.keys(value)) result[key] = deepClone(value[key]);
	return result;
}
function toPath(value) {
	if (Array.isArray(value)) return value;
	return value.replace(/\[(\d+)]/g, ".$1").replace(/^\./, "").replace(/\.$/, "").split(".");
}
var pick = (obj, ...args) => ({ ...args.reduce((res, key) => ({
	...res,
	[key]: obj[key]
}), {}) });
function isObject(item) {
	return !!item && typeof item === "object" && !Array.isArray(item);
}
function isPlainObject(obj) {
	if (typeof obj !== "object" || obj === null || Array.isArray(obj)) return false;
	return Object.getPrototypeOf(obj) === Object.prototype;
}
function mergeDeep(target, source, ignoreUndefined = false) {
	if (!isObject(target)) return target;
	const output = { ...target };
	if (!isObject(source)) return output;
	for (const key in source) {
		if (key === "__proto__" || key === "constructor" || key === "prototype") continue;
		if (Object.prototype.hasOwnProperty.call(source, key)) {
			const sourceValue = source[key];
			const outputValue = output[key];
			if (ignoreUndefined && sourceValue === void 0) continue;
			if (sourceValue instanceof Date) output[key] = new Date(sourceValue.getTime());
			else if (Array.isArray(sourceValue)) if (Array.isArray(outputValue)) if (!(sourceValue.some(isPlainObject) || outputValue.some(isPlainObject))) output[key] = [...sourceValue];
			else {
				const newArray = [];
				const maxLength = Math.max(outputValue.length, sourceValue.length);
				for (let i = 0; i < maxLength; i++) {
					const sourceItem = sourceValue[i];
					const targetItem = outputValue[i];
					if (i >= sourceValue.length) newArray[i] = targetItem;
					else if (i >= outputValue.length) newArray[i] = sourceItem;
					else if (sourceItem === null) newArray[i] = targetItem;
					else if (isPlainObject(sourceItem) && isPlainObject(targetItem)) newArray[i] = mergeDeep(targetItem, sourceItem, ignoreUndefined);
					else newArray[i] = sourceItem;
				}
				output[key] = newArray;
			}
			else output[key] = [...sourceValue];
			else if (isPlainObject(sourceValue)) if (isPlainObject(outputValue)) output[key] = mergeDeep(outputValue, sourceValue, ignoreUndefined);
			else output[key] = sourceValue;
			else if (isObject(sourceValue)) output[key] = sourceValue;
			else output[key] = sourceValue;
		}
	}
	return output;
}
function getValueInPath(o, path) {
	if (!o) return void 0;
	if (typeof o === "object") {
		if (path in o) return o[path];
		if (path.includes(".") || path.includes("[")) {
			let pathSegments = path.split(/[.[]/);
			if (path.includes("[")) pathSegments = pathSegments.map((segment) => segment.replace("]", ""));
			const firstSegment = pathSegments[0];
			const isArrayAndIndexExists = Array.isArray(o[firstSegment]) && !isNaN(parseInt(pathSegments[1]));
			const nextObject = isArrayAndIndexExists ? o[firstSegment][parseInt(pathSegments[1])] : o[firstSegment];
			const nextPath = pathSegments.slice(isArrayAndIndexExists ? 2 : 1).join(".");
			if (nextPath === "") return nextObject;
			return getValueInPath(nextObject, nextPath);
		}
	}
}
function removeInPath(o, path) {
	const res = clone(o);
	let current = res;
	const parts = path.split(".");
	const last = parts.pop();
	for (const part of parts) if (part in current && current[part] !== null && typeof current[part] === "object") {
		current[part] = clone(current[part]);
		current = current[part];
	} else return res;
	if (last && current && typeof current === "object") delete current[last];
	return res;
}
function removeFunctions(o) {
	if (o === void 0) return void 0;
	if (o === null) return null;
	if (typeof o === "object") {
		if (Array.isArray(o)) return o.filter((v) => typeof v !== "function").map((v) => removeFunctions(v));
		if (!isPlainObject(o)) return o;
		return Object.entries(o).filter(([_, value]) => typeof value !== "function").reduce((acc, [key, value]) => {
			acc[key] = removeFunctions(value);
			return acc;
		}, {});
	}
	return o;
}
function getHashValue(v) {
	if (!v) return null;
	if (typeof v === "object" && v !== null) {
		if ("id" in v) return String(v.id);
		else if (v instanceof Date) return v.toLocaleString();
		else if (v instanceof GeoPoint) return hash(v);
	}
	return hash(v, { ignoreUnknown: true });
}
function removeUndefined(value, removeEmptyStrings) {
	if (typeof value === "function") return value;
	if (Array.isArray(value)) return value.map((v) => removeUndefined(v, removeEmptyStrings));
	if (typeof value === "object") {
		if (value === null) return value;
		if (!isPlainObject(value)) return value;
		const res = {};
		Object.keys(value).forEach((key) => {
			if (!isEmptyObject(value)) {
				const childRes = removeUndefined(value[key], removeEmptyStrings);
				const isString = typeof childRes === "string";
				const shouldKeepIfString = !removeEmptyStrings || removeEmptyStrings && !isString || removeEmptyStrings && isString && childRes !== "";
				if (childRes !== void 0 && !isEmptyObject(childRes) && shouldKeepIfString) res[key] = childRes;
			}
		});
		return res;
	}
	return value;
}
function removeNulls(value) {
	if (typeof value === "function") return value;
	if (Array.isArray(value)) return value.map((v) => removeNulls(v));
	if (typeof value === "object") {
		if (value === null) return value;
		if (!isPlainObject(value)) return value;
		const res = {};
		const obj = value;
		Object.keys(obj).forEach((key) => {
			if (obj[key] !== null) res[key] = removeNulls(obj[key]);
		});
		return res;
	}
	return value;
}
function isEmptyObject(obj) {
	return obj && Object.getPrototypeOf(obj) === Object.prototype && Object.keys(obj).length === 0;
}
function removePropsIfExisting(source, comparison) {
	const isObject = (val) => typeof val === "object" && val !== null;
	const isArray = (val) => Array.isArray(val);
	if (!isObject(source) || !isObject(comparison)) return source;
	const res = isArray(source) ? [...source] : { ...source };
	if (isArray(res)) {
		const at = (index) => isArray(comparison) ? comparison[index] : comparison[String(index)];
		for (let i = res.length - 1; i >= 0; i--) {
			const mine = res[i];
			const theirs = at(i);
			if (mine === theirs) res.splice(i, 1);
			else if (isObject(mine) && isObject(theirs)) res[i] = removePropsIfExisting(mine, theirs);
		}
	} else Object.keys(comparison).forEach((key) => {
		if (key in res) {
			if (isObject(res[key]) && isObject(comparison[key])) res[key] = removePropsIfExisting(res[key], comparison[key]);
			else if (res[key] === comparison[key]) delete res[key];
		}
	});
	return res;
}
//#endregion
//#region src/arrays.ts
/**
* Normalise a value that may be a single item or a list into a list.
*
* Only `null`/`undefined` mean "nothing". A truthiness check here silently
* swallowed legitimate values — `toArray(0)`, `toArray(false)` and `toArray("")`
* all came back empty, so a caller normalising a single falsy item lost it.
*/
function toArray(input) {
	if (Array.isArray(input)) return input;
	if (input === void 0 || input === null) return [];
	return [input];
}
//#endregion
//#region src/dates.ts
var defaultDateFormat = "MMMM dd, yyyy, HH:mm:ss";
/** Seven days, the distance past which a relative phrase stops being useful. */
var DEFAULT_MAX_MS = 10080 * 60 * 1e3;
function toTime(value) {
	if (value === null || value === void 0 || value === "") return null;
	const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
	return Number.isNaN(time) ? null : time;
}
/**
* Describes an instant relative to another one — "5m ago", "in 3h".
*
* The direction is part of the answer. Every hand-rolled version of this in the
* codebase computed `now - then` and then tested only the positive side, so a
* timestamp in the future fell through to whichever branch happened to be
* first: a date scheduled for next month read "Just now", and one a couple of
* hours out read "-1d ago". Both are dates a CMS holds all the time — a publish
* date, a due date, an expiry — and neither shape can occur here, because the
* distance is measured with {@link Math.abs} and the tense is chosen from the
* sign rather than assumed.
*
* Returns `null` when the value is unreadable, or when it is further than
* {@link FormatRelativeTimeOptions.maxMs} away in either direction. `null` is
* "say it another way", not an error: the caller owns the absolute format, and
* the locale and precision that go with it.
*/
function formatRelativeTime(value, options = {}) {
	const then = toTime(value);
	if (then === null) return null;
	const now = options.now instanceof Date ? options.now.getTime() : options.now ?? Date.now();
	const maxMs = options.maxMs ?? DEFAULT_MAX_MS;
	const delta = now - then;
	const distance = Math.abs(delta);
	if (distance > maxMs) return null;
	const future = delta < 0;
	const minutes = Math.floor(distance / 6e4);
	if (minutes < 1) return future ? "in a moment" : "just now";
	if (minutes < 60) return future ? `in ${minutes}m` : `${minutes}m ago`;
	const hours = Math.floor(distance / 36e5);
	if (hours < 24) return future ? `in ${hours}h` : `${hours}h ago`;
	const days = Math.floor(distance / 864e5);
	return future ? `in ${days}d` : `${days}d ago`;
}
//#endregion
//#region src/storage.ts
/**
* The ambient `localStorage`, or `null` where there is not one. Access itself
* is what throws when storage is disabled, so even reaching for it is guarded.
*/
function getWebStorage() {
	try {
		return globalThis.localStorage ?? null;
	} catch {
		return null;
	}
}
/**
* Reads and parses a JSON value a previous session stored, falling back rather
* than throwing. See the module comment for what it is falling back from.
*
* A rejected value is deliberately left in place rather than cleared: this
* version not understanding it is not evidence that nothing does.
*/
function readStoredJson(key, options) {
	const storage = options.storage === void 0 ? getWebStorage() : options.storage;
	if (!storage) return options.fallback;
	let raw;
	try {
		raw = storage.getItem(key);
	} catch {
		return options.fallback;
	}
	if (raw === null || raw === "") return options.fallback;
	let parsed;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return options.fallback;
	}
	if (options.accept && !options.accept(parsed)) return options.fallback;
	return parsed;
}
/**
* Persists a value as JSON. Returns whether it was stored, so a caller that
* cares can say so — most do not, and for them the point is simply that a full
* quota does not throw out of the effect doing the writing.
*/
function writeStoredJson(key, value, options = {}) {
	const storage = options.storage === void 0 ? getWebStorage() : options.storage;
	if (!storage) return false;
	try {
		storage.setItem(key, JSON.stringify(value));
		return true;
	} catch {
		return false;
	}
}
/**
* Persists an already-serialised string, for the values kept as plain text
* rather than JSON — a selected id, a pane size.
*/
function writeStoredString(key, value, options = {}) {
	const storage = options.storage === void 0 ? getWebStorage() : options.storage;
	if (!storage) return false;
	try {
		storage.setItem(key, value);
		return true;
	} catch {
		return false;
	}
}
/** Reads a plain string, absent rather than throwing where there is no storage. */
function readStoredString(key, options = {}) {
	const storage = options.storage === void 0 ? getWebStorage() : options.storage;
	if (!storage) return null;
	try {
		return storage.getItem(key);
	} catch {
		return null;
	}
}
/** `accept` for a caller whose fallback is an array. */
var isArrayValue = (value) => Array.isArray(value);
/** `accept` for a caller whose fallback is a keyed object — and not an array. */
var isRecordValue = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
//#endregion
//#region src/hash.ts
function hashString(str) {
	if (!str) return 0;
	let hash = 0;
	let i;
	let chr;
	for (i = 0; i < str.length; i++) {
		chr = str.charCodeAt(i);
		hash = (hash << 5) - hash + chr;
		hash |= 0;
	}
	return Math.abs(hash);
}
//#endregion
//#region src/sha1.ts
/**
* Minimal SHA-1 implementation that runs in both Node and the browser.
*
* This exists because generated Postgres policy names embed a SHA-1 digest of
* the security rule. The DDL generator runs on the server (where `node:crypto`
* is available) but the Studio has to derive the same names in the browser to
* tell a policy it generated apart from one it did not. `node:crypto` cannot be
* bundled for the browser, so the shared derivation needs a portable digest.
*
* SHA-1 is used purely to name things deterministically — never for security.
* The output is byte-identical to `createHash("sha1").update(str).digest("hex")`,
* which `sha1.test.ts` pins against `node:crypto` directly.
*/
/** Rotate a 32-bit word left by `n` bits. */
function rotl(value, n) {
	return value << n | value >>> 32 - n;
}
/**
* SHA-1 digest of a string, hex-encoded.
*
* The input is encoded as UTF-8, matching Node's default handling of strings
* passed to `hash.update(str)`.
*/
function sha1Hex(input) {
	const bytes = Array.from(new TextEncoder().encode(input));
	const bitLength = bytes.length * 8;
	bytes.push(128);
	while (bytes.length % 64 !== 56) bytes.push(0);
	const hi = Math.floor(bitLength / 4294967296);
	const lo = bitLength >>> 0;
	bytes.push(hi >>> 24 & 255, hi >>> 16 & 255, hi >>> 8 & 255, hi & 255);
	bytes.push(lo >>> 24 & 255, lo >>> 16 & 255, lo >>> 8 & 255, lo & 255);
	let h0 = 1732584193;
	let h1 = 4023233417;
	let h2 = 2562383102;
	let h3 = 271733878;
	let h4 = 3285377520;
	const w = new Array(80);
	for (let offset = 0; offset < bytes.length; offset += 64) {
		for (let i = 0; i < 16; i++) {
			const j = offset + i * 4;
			w[i] = bytes[j] << 24 | bytes[j + 1] << 16 | bytes[j + 2] << 8 | bytes[j + 3] | 0;
		}
		for (let i = 16; i < 80; i++) w[i] = rotl(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
		let a = h0;
		let b = h1;
		let c = h2;
		let d = h3;
		let e = h4;
		for (let i = 0; i < 80; i++) {
			let f;
			let k;
			if (i < 20) {
				f = b & c | ~b & d;
				k = 1518500249;
			} else if (i < 40) {
				f = b ^ c ^ d;
				k = 1859775393;
			} else if (i < 60) {
				f = b & c | b & d | c & d;
				k = 2400959708;
			} else {
				f = b ^ c ^ d;
				k = 3395469782;
			}
			const temp = rotl(a, 5) + f + e + k + w[i] | 0;
			e = d;
			d = c;
			c = rotl(b, 30);
			b = a;
			a = temp;
		}
		h0 = h0 + a | 0;
		h1 = h1 + b | 0;
		h2 = h2 + c | 0;
		h3 = h3 + d | 0;
		h4 = h4 + e | 0;
	}
	return [
		h0,
		h1,
		h2,
		h3,
		h4
	].map((word) => (word >>> 0).toString(16).padStart(8, "0")).join("");
}
//#endregion
//#region src/policy-names.ts
/**
* Naming of the Postgres policies generated from a collection's security rules.
*
* A rule without an explicit `name` is compiled to `<table>_<op>_<hash>`, where
* the hash covers the rule's semantics. The Studio needs the same names to tell
* "this policy came from your code" apart from "someone wrote this in SQL" —
* without them it treats generated policies as foreign and offers to import
* them back into the codebase they came from.
*
* This is the single definition of that naming. The DDL and Drizzle generators
* both derive names from here, so a change cannot silently rename every policy
* in every deployed database while the UI keeps matching the old ones.
*/
/** Stable digest of the parts of a rule that determine what the policy does. */
function getPolicyNameHash(rule) {
	return sha1Hex(JSON.stringify({
		a: rule.access,
		m: rule.mode,
		op: rule.operation,
		ops: rule.operations?.slice().sort(),
		own: rule.ownerField,
		rol: rule.roles?.slice().sort(),
		pg: rule.pgRoles?.slice().sort(),
		u: rule.using,
		w: rule.withCheck,
		c: rule.condition,
		ch: rule.check
	})).substring(0, 7);
}
/** The operations a rule expands to — `operations` wins over `operation`. */
function getPolicyOperations(rule) {
	return rule.operations && rule.operations.length > 0 ? rule.operations : [rule.operation ?? "all"];
}
/**
* Every Postgres policy name a single rule compiles to — one per operation.
*
* @param rule      The security rule as written in the collection config.
* @param tableName The rule's table (see `getTableName` in `@rebasepro/common`).
*/
function getPolicyNamesForRule(rule, tableName) {
	const ops = getPolicyOperations(rule);
	const ruleHash = getPolicyNameHash(rule);
	return ops.map((op, opIdx) => rule.name ? ops.length > 1 ? `${rule.name}_${op}` : rule.name : `${tableName}_${op}_${ruleHash}${ops.length > 1 ? `_${opIdx}` : ""}`);
}
/** Every policy name a set of rules compiles to, for membership checks. */
function getPolicyNamesForRules(rules, tableName) {
	const names = /* @__PURE__ */ new Set();
	for (const rule of rules) for (const name of getPolicyNamesForRule(rule, tableName)) names.add(name);
	return names;
}
//#endregion
//#region src/regexp.ts
function serializeRegExp(input) {
	if (!input) return "";
	return input.toString();
}
/**
* Get a RegExp out of a serialized string
* @param input
*/
function hydrateRegExp(input) {
	if (!input) return void 0;
	const fragments = input.match(/\/(.*?)\/([a-z]*)?$/i);
	if (fragments) return new RegExp(fragments[1], fragments[2] || "");
	else return new RegExp(input, "");
}
/**
* Is `input` something {@link hydrateRegExp} can turn into a working RegExp?
*
* This used to pattern-match the *shape* of a regex literal and, failing that,
* fall back to "does it contain any regex-ish character" — which said yes to
* malformed input like `/[a-z/g`. The only answer that matters to a caller is
* whether hydration succeeds, so ask the engine instead of approximating it.
*/
function isValidRegExp(input) {
	if (!input) return false;
	try {
		return hydrateRegExp(input) !== void 0;
	} catch {
		return false;
	}
}
//#endregion
//#region src/flatten_object.ts
function flattenObject(obj, parentKey = "") {
	if (!obj) return obj;
	return Object.keys(obj).reduce((flatObj, key) => {
		const newKey = parentKey ? `${parentKey}.${key}` : key;
		if (typeof obj[key] === "object" && obj[key] !== null) if (Array.isArray(obj[key])) obj[key].forEach((item, index) => {
			if (typeof item === "object" && item !== null) Object.assign(flatObj, flattenObject(item, `${newKey}[${index}]`));
			else flatObj[`${newKey}[${index}]`] = item;
		});
		else Object.assign(flatObj, flattenObject(obj[key], newKey));
		else flatObj[newKey] = obj[key];
		return flatObj;
	}, {});
}
function getArrayValuesCount(array) {
	return array.reduce((acc, obj) => {
		Object.entries(obj).forEach(([key, value]) => {
			if (Array.isArray(value)) acc[key] = Math.max(acc[key] || 0, value.length);
			if (typeof value === "object" && value !== null) {
				const nested = getArrayValuesCount([value]);
				Object.entries(nested).forEach(([nestedKey, nestedCount]) => {
					const compoundKey = `${key}.${nestedKey}`;
					acc[compoundKey] = Math.max(acc[compoundKey] || 0, nestedCount);
				});
			}
		});
		return acc;
	}, {});
}
//#endregion
//#region src/plurals.ts
/**
* Returns the plural of an English word.
*
* @param {string} word
* @param {number} [amount]
* @returns {string}
*/
function plural(word, amount) {
	if (amount !== void 0 && amount === 1) return word;
	const plurals = {
		"(quiz)$": "$1zes",
		"^(ox)$": "$1en",
		"([m|l])ouse$": "$1ice",
		"(matr|vert|ind)ix|ex$": "$1ices",
		"(x|ch|ss|sh)$": "$1es",
		"([^aeiouy]|qu)y$": "$1ies",
		"(hive)$": "$1s",
		"(?:([^f])fe|([lr])f)$": "$1$2ves",
		"(shea|lea|loa|thie)f$": "$1ves",
		sis$: "ses",
		"([ti])um$": "$1a",
		"(tomat|potat|ech|her|vet)o$": "$1oes",
		"(bu)s$": "$1ses",
		"(alias)$": "$1es",
		"(octop)us$": "$1i",
		"(ax|test)is$": "$1es",
		"(us)$": "$1es",
		"([^s]+)$": "$1s"
	};
	const irregular = {
		move: "moves",
		foot: "feet",
		goose: "geese",
		sex: "sexes",
		child: "children",
		man: "men",
		tooth: "teeth",
		person: "people"
	};
	if ([
		"sheep",
		"fish",
		"deer",
		"moose",
		"series",
		"species",
		"money",
		"rice",
		"information",
		"equipment",
		"bison",
		"cod",
		"offspring",
		"pike",
		"salmon",
		"shrimp",
		"swine",
		"trout",
		"aircraft",
		"hovercraft",
		"spacecraft",
		"sugar",
		"tuna",
		"you",
		"wood"
	].indexOf(word.toLowerCase()) >= 0) return word;
	for (const w in irregular) {
		const pattern = new RegExp(`${w}$`, "i");
		const replace = irregular[w];
		if (pattern.test(word)) return word.replace(pattern, replace);
	}
	for (const reg in plurals) {
		const pattern = new RegExp(reg, "i");
		if (pattern.test(word)) return word.replace(pattern, plurals[reg]);
	}
	return word;
}
/**
* Returns the singular of an English word.
*
* @param {string} word
* @param {number} [amount]
* @returns {string}
*/
function singular(word, amount) {
	if (amount !== void 0 && amount !== 1) return word;
	const singulars = {
		"(quiz)zes$": "$1",
		"(matr)ices$": "$1ix",
		"(vert|ind)ices$": "$1ex",
		"^(ox)en$": "$1",
		"(alias)es$": "$1",
		"(octop|vir)i$": "$1us",
		"(cris|ax|test)es$": "$1is",
		"(shoe)s$": "$1",
		"(o)es$": "$1",
		"(bus)es$": "$1",
		"([m|l])ice$": "$1ouse",
		"(x|ch|ss|sh)es$": "$1",
		"(m)ovies$": "$1ovie",
		"(s)eries$": "$1eries",
		"([^aeiouy]|qu)ies$": "$1y",
		"([lr])ves$": "$1f",
		"(tive)s$": "$1",
		"(hive)s$": "$1",
		"(li|wi|kni)ves$": "$1fe",
		"(shea|loa|lea|thie)ves$": "$1f",
		"(^analy)ses$": "$1sis",
		"((a)naly|(b)a|(d)iagno|(p)arenthe|(p)rogno|(s)ynop|(t)he)ses$": "$1$2sis",
		"([ti])a$": "$1um",
		"(n)ews$": "$1ews",
		"(h|bl)ouses$": "$1ouse",
		"(corpse)s$": "$1",
		"(us)es$": "$1",
		s$: ""
	};
	const irregular = {
		move: "moves",
		foot: "feet",
		goose: "geese",
		sex: "sexes",
		child: "children",
		man: "men",
		tooth: "teeth",
		person: "people"
	};
	if ([
		"sheep",
		"fish",
		"deer",
		"moose",
		"series",
		"species",
		"money",
		"rice",
		"information",
		"equipment",
		"bison",
		"cod",
		"offspring",
		"pike",
		"salmon",
		"shrimp",
		"swine",
		"trout",
		"aircraft",
		"hovercraft",
		"spacecraft",
		"sugar",
		"tuna",
		"you",
		"wood"
	].indexOf(word.toLowerCase()) >= 0) return word;
	for (const w in irregular) {
		const pattern = new RegExp(`${irregular[w]}$`, "i");
		if (pattern.test(word)) return word.replace(pattern, w);
	}
	for (const reg in singulars) {
		const pattern = new RegExp(reg, "i");
		if (pattern.test(word)) return word.replace(pattern, singulars[reg]);
	}
	return word;
}
//#endregion
//#region src/names.ts
/**
* Generates a foreign key column name from a given string, typically a collection slug or name.
* It singularizes the name, converts it to snake_case and appends '_id'.
*
* Singularization runs *before* snake-casing so that acronyms survive: `toSnakeCase`
* splits on every capital, which turned "URLs" into "ur_ls" and then "ur_l_id".
*
* @param name The base name to convert to a foreign key.
* @returns A foreign key name in the format 'singular_name_id'.
*
* @example
* // returns "user_id"
* generateForeignKeyName("users")
*
* @example
* // returns "category_id"
* generateForeignKeyName("categories")
*
* @example
* // returns "product_id"
* generateForeignKeyName("Product")
*
*/
function generateForeignKeyName(name) {
	return `${toSnakeCase(singularizeForKey(name))}_id`;
}
/**
* `singular()` handles real English plurals, but its final catch-all rule strips
* any trailing "s", which mangles words that only look plural. Guard the two
* cases that produce a column name nobody would recognise:
*
* - a double "s" ending is never a plural marker ("address", "class", "process"),
*   so stripping it yields "addres";
* - a name that singularizes to nothing (the literal "s") would yield "_id".
*/
function singularizeForKey(name) {
	if (/ss$/i.test(name)) return name;
	const result = singular(name);
	return result.length > 0 ? result : name;
}
/**
* What `generateForeignKeyName` returned before it learned to singularize:
* snake-case the name, then chop one trailing "s".
*
* This is here to be *detected*, never to be generated. A database provisioned
* under the old rule carries `categorie_id`, `addresse_id`, `children_id` or
* `ur_l_id` where the current rule expects `category_id`, `address_id`,
* `child_id` and `url_id` — and the boot-time schema ensure is additive, so it
* would create the new column empty beside the populated old one and leave the
* relation reading nothing. No error, no missing table: the failure is silent,
* which is the only reason this function still exists.
*
* `ensureCollectionSchema` calls it to recognise that shape and say so.
* Returns the same string as `generateForeignKeyName` for every regular plural,
* so a caller can compare the two and act only when they differ.
*/
function legacyForeignKeyName(name) {
	const snake = toSnakeCase(name);
	return `${snake.endsWith("s") ? snake.slice(0, -1) : snake}_id`;
}
/**
* Truncate an identifier to what Postgres will actually store.
*
* Postgres silently truncates identifiers at NAMEDATALEN-1 = 63 **bytes**, so a
* name generated longer than that is not the name the database ends up holding.
* Anything that later looks the object up by the name it generated then misses.
*
* Byte length, not string length: NAMEDATALEN is a byte bound, and a multi-byte
* character straddling the boundary would be cut mid-sequence by `slice(0, 63)`.
*
* `TextEncoder` rather than `Buffer`, which is not a matter of taste: `Buffer`
* is a Node global, and this package is imported by browser-facing ones. It
* typechecked only where `@types/node` happened to be in scope, so
* `packages/codegen` — whose tsconfig is `lib: ["ESNext", "dom"]` — could not
* compile the file at all, and both of its suites failed to run. `TextEncoder`
* and `TextDecoder` are standard in both runtimes and need no ambient types.
*/
function toPostgresIdentifier(name) {
	return truncateToBytes(name, 63);
}
/**
* {@link toPostgresIdentifier} with the bound lifted to a parameter.
*
* Exists for names that end in something load-bearing. Truncating at 63 keeps
* the *head* of a name and discards the tail, which is right for a descriptive
* identifier and wrong for a hashed one: the hash is the part that makes it
* unique, and it is at the end. A caller that appends a fingerprint truncates
* the readable head to `63 - <tail>` itself and then appends, so the bound is
* still 63 and the hash always survives.
*
* `contracts/derived-names.txt` records what the alternative costs — a foreign
* key frozen as `..._corres`, its `_fkey` suffix truncated away, so a second
* foreign key on that table would derive a byte-identical name.
*
* One truncation rule, in one function, so the two cannot drift.
*/
function truncateToBytes(name, maxBytes) {
	const bytes = new TextEncoder().encode(name);
	if (bytes.byteLength <= maxBytes) return name;
	return new TextDecoder("utf-8").decode(bytes.subarray(0, maxBytes)).replace(/�+$/, "");
}
/**
* The API name a database column is served under.
*
* The wire name of a field is its property key, and Rebase's property keys are
* camelCase — `displayName`, `createdAt`, `photoURL`. Columns are snake_case,
* because an unquoted Postgres identifier folds to lower case and a camelCase
* column is therefore reachable only as `"authorId"` forever: in hand-written
* SQL, in psql, in an RLS policy body, in a dump, and in every third-party tool
* that ever touches the database. So the two conventions are both right, and
* this is the function that crosses between them.
*
* It exists because two sources of field names never crossed: a foreign key
* derived from a relation (`author_id`) and a column read back by introspection
* (`user_id`) both landed on the wire under their column name, while every
* hand-authored collection next to them used camelCase. One API, two
* conventions, and no rule a caller could infer from outside — those names are
* also the `where` and `orderBy` keys, so it was not a matter of taste.
*
* Rules, in the order they matter:
*
*  - **A name with no separator is returned unchanged.** `photoURL` stays
*    `photoURL` and `id` stays `id`. Lower-casing a single token is what makes
*    a "camelCase" helper destructive — `camelCase("photoURL")` is `photourl` —
*    and this function is applied to names that are *already* keys.
*  - **Each following segment keeps its own casing** apart from an upper-cased
*    first letter, so `photo_URL` → `photoURL` rather than `photoUrl`.
*  - **The result may still not be a JavaScript identifier.** `2fa_enabled`
*    becomes `2faEnabled`, which is a perfectly good object key and still needs
*    quoting where one is written into generated source.
*
* Not the inverse of {@link toSnakeCase}: `toSnakeCase` tokenises on case
* boundaries and would turn `photoURL` into `photo_url`. Round-tripping is not
* a property either function promises, which is why a column name that a
* property maps explicitly is always read off `columnName` rather than derived.
*/
function toWireKey(columnName) {
	if (!columnName) return columnName;
	const segments = columnName.split(/[-_ ]+/).filter(Boolean);
	if (segments.length <= 1) return columnName;
	return segments.map((segment, index) => index === 0 ? segment.charAt(0).toLowerCase() + segment.slice(1) : segment.charAt(0).toUpperCase() + segment.slice(1)).join("");
}
/**
* The first candidate key not already used, or a numbered fallback.
*
* Introspection turns a set of column names into a set of object keys, and the
* mapping is not injective: `user_id` and `userId` are two columns and one
* {@link toWireKey}, and two foreign keys can strip to the same relation name.
* A duplicate key in a generated object literal is a TypeScript error, so the
* whole collection stops compiling — and a duplicate key in a `Record` built at
* runtime is worse, because it silently drops a column instead.
*
* The numbered tail is what makes this total: a function that returns a key it
* cannot guarantee is free has only moved the duplicate one line down.
*
* Structurally typed on `has` so a `Map` of emitted blocks and a `Set` of taken
* names both satisfy it. Lives here, in the package both introspection
* producers and the admin's table import can reach, because they must resolve a
* collision the same way or one database describes itself three ways.
*/
function firstFreeKey(candidates, taken) {
	for (const candidate of candidates) if (!taken.has(candidate)) return candidate;
	const base = candidates[candidates.length - 1];
	for (let suffix = 2;; suffix++) {
		const candidate = `${base}_${suffix}`;
		if (!taken.has(candidate)) return candidate;
	}
}
//#endregion
//#region src/fields.ts
function isDefaultFieldConfigId(id) {
	return [
		"text_field",
		"multiline",
		"markdown",
		"url",
		"email",
		"switch",
		"select",
		"multi_select",
		"number_input",
		"number_select",
		"multi_number_select",
		"file_upload",
		"multi_file_upload",
		"reference",
		"multi_references",
		"relation",
		"date_time",
		"group",
		"key_value",
		"repeat",
		"custom_array",
		"block"
	].includes(id);
}
//#endregion
//#region src/unref.ts
/**
* Release a Node handle's hold on the event loop, where the runtime has one.
*
* `unref` is Node's, not the web platform's. Node ref-counts the handles that
* can keep a process alive — timers, `BroadcastChannel`, sockets — and gives
* each an `unref()` that says "you are still live, but do not be the reason
* this process refuses to exit". The browser's equivalents have no such method:
* `setTimeout` there returns a plain number, and nothing is holding a page open
* in the first place.
*
* So the call is genuinely conditional at runtime, in every package that runs
* in both places. Spelled out at the call site it came to
* `(handle as unknown as { unref?: () => void }).unref?.()` — a double cast,
* because where the DOM declaration of `setTimeout` wins the handle is a
* `number`, and `number` does not overlap an object type, so a single `as` is
* refused. The `unknown` in the middle was there to make the refusal go away,
* and it also discarded the check on the member's name and signature. There
* were seven copies, which is seven chances to get one of those wrong.
*
* Takes `unknown` on purpose: the argument's type is exactly what is not known
* here, and narrowing it to a timer would be a claim this cannot make about a
* `BroadcastChannel`. A no-op on a browser handle, on `null`/`undefined`, and
* on a Node timer that has already fired.
*/
function unref(handle) {
	if (handle === null || typeof handle !== "object") return;
	const fn = handle.unref;
	if (typeof fn === "function") fn.call(handle);
}
//#endregion
export { MAX_SEARCH_TERMS, camelCase, clone, deepClone, defaultDateFormat, firstFreeKey, flattenObject, formatRelativeTime, generateForeignKeyName, getArrayValuesCount, getHashValue, getIn, getPolicyNameHash, getPolicyNamesForRule, getPolicyNamesForRules, getPolicyOperations, getValueInPath, getWebStorage, hashString, hydrateRegExp, isArrayValue, isDefaultFieldConfigId, isEmptyArray, isEmptyObject, isFunction, isInteger, isNaN, isNearMiss, isObject, isPlainObject, isPrototypePollutingKey, isRecordValue, isValidRegExp, legacyForeignKeyName, mergeDeep, pathTraversesPrototype, pick, plural, prettifyIdentifier, randomColor, randomString, readStoredJson, readStoredString, removeFunctions, removeInPath, removeNulls, removePropsIfExisting, removeUndefined, serializeRegExp, setIn, sha1Hex, singular, slugify, splitSearchTerms, suggestNearMiss, toArray, toKebabCase, toPostgresIdentifier, toSnakeCase, toWireKey, truncateToBytes, unref, unslugify, writeStoredJson, writeStoredString };

//# sourceMappingURL=index.es.js.map